import React, { useEffect, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronRightIcon, HomeIcon, TrashIcon } from '@heroicons/react/20/solid';

import { useAuth } from '../../hooks/use-auth';
import CommonButtonDefault from '../../components/common/buttons/default';
import CommonAnimatedLoader from '../../components/common/animated/loader';
import EmployeeForm from '../../entities/form/employee';
import * as employeesService from '../../services/api/employees';
import ValidationError from '../../entities/validation-error';
import LabelWithError from '../../components/common/form/label-with-error';
import StyledInput from '../../components/common/form/styled-input';
import { strings } from '../../localization/strings';
import CommonConfirmationModal from '../../components/modals/confirmation';
import StyledSwitch from '../../components/common/form/styled-switch';
import Heading1 from '../../components/common/headings/heading1';
import { Role } from '../../entities/role';
import * as employeeRolesService from '../../services/api/employee-roles';
import { getEmployeeRoleLabel } from '../../services/employee-role-label';

interface ComponentProps {
  isEdit?: boolean;
}

function EmployeesEditPageComponent({ isEdit }: ComponentProps) {
  const defaultEmployeeCompanyId = 1;
  const { token } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const {
    register,
    formState: { errors },
    clearErrors,
    setError,
    control,
    reset,
    setValue,
    handleSubmit,
  } = useForm<EmployeeForm>({
    defaultValues: {
      ips: [],
      roles: [],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'ips' });

  const onDelete = async () => {
    if (id) {
      setIsLoading(true);
      await employeesService.remove(token, parseInt(id, 10));

      navigate('/employees');
    }
  };

  const onSubmit = async (data: EmployeeForm) => {
    setIsLoading(true);
    clearErrors();

    try {
      if (isEdit && id) {
        await employeesService.update(token, parseInt(id, 10), data);
      } else {
        await employeesService.create(token, {
          ...data,
          company_id: defaultEmployeeCompanyId,
        });
      }

      navigate('/employees?success=1');
    } catch (e) {
      setIsLoading(false);

      const err = e as ValidationError<EmployeeForm>;

      err.errors.forEach((item) => {
        setError(item.key, { message: item.value });
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const roles = await employeeRolesService.list(token);

        setAvailableRoles(roles);

        if (isEdit && id) {
          const item = await employeesService.one(token, id);

          reset({
            ...item,
            company_id: item.company_id,
            roles: item.roles?.map((role) => role.name) || [],
          });
        } else {
          setValue('is_active', 1);
          setValue('is_group', 0);
          setValue('roles', []);
          setValue('company_id', defaultEmployeeCompanyId);
        }

        setIsLoading(false);
      } catch {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [id, isEdit, reset, setValue, token]);

  return (
    <div>
      {isLoading && <CommonAnimatedLoader />}
      {!isLoading && (
        <>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <div>
                  <a href="/" className="text-gray-400 hover:text-gray-500">
                    <HomeIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
                    <span className="sr-only">Home</span>
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <a href="/employees" className="ml-4 text-sm font-medium text-gray-500 hover:text-gray-700">
                    {strings.navigation.employees}
                  </a>
                </div>
              </li>
              <li>
                <div className="flex items-center">
                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-gray-400" aria-hidden="true" />
                  <span className="ml-4 text-sm font-medium text-gray-500" aria-current="page">
                    {isEdit ? strings.employees.editTitle : strings.employees.createTitle}
                  </span>
                </div>
              </li>
            </ol>
          </nav>
          <header className="mb-3 mt-3">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <Heading1>{isEdit ? strings.employees.editTitle : strings.employees.createTitle}</Heading1>
            </div>
          </header>
          <main>
            <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="border-b border-gray-200 bg-white shadow sm:rounded-lg"
              >
                <input type="hidden" {...register('company_id', { valueAsNumber: true, required: true })} />
                <div className="grid grid-cols-1 gap-x-6 gap-y-6 p-4 sm:grid-cols-12">
                  <div className="sm:col-span-3">
                    <LabelWithError title={strings.employees.form.firstName} error={errors.first_name?.message}>
                      <StyledInput
                        type="text"
                        {...register('first_name', { required: true })}
                        hasError={!!errors.first_name}
                      />
                    </LabelWithError>
                  </div>
                  <div className="sm:col-span-3">
                    <LabelWithError title={strings.employees.form.lastName} error={errors.last_name?.message}>
                      <StyledInput
                        type="text"
                        {...register('last_name', { required: true })}
                        hasError={!!errors.last_name}
                      />
                    </LabelWithError>
                  </div>
                  <div className="sm:col-span-3">
                    <LabelWithError title={strings.employees.form.username} error={errors.username?.message}>
                      <StyledInput
                        type="text"
                        {...register('username', { required: true })}
                        hasError={!!errors.username}
                      />
                    </LabelWithError>
                  </div>
                  <div className="sm:col-span-3">
                    <LabelWithError title={strings.employees.form.email} error={errors.email?.message}>
                      <StyledInput
                        type="email"
                        {...register('email', {
                          required: true,
                          pattern: {
                            value: /\S+@\S+\.\S+/,
                            message: strings.form.error.emailFormat,
                          },
                        })}
                        hasError={!!errors.email}
                      />
                    </LabelWithError>
                  </div>
                  <div className="sm:col-span-3">
                    <LabelWithError title={strings.employees.form.position} error={errors.position?.message}>
                      <StyledInput type="text" {...register('position')} hasError={!!errors.position} />
                    </LabelWithError>
                  </div>
                  <div className="sm:col-span-3">
                    <LabelWithError title={strings.employees.form.phone} error={errors.phone?.message}>
                      <StyledInput type="text" {...register('phone')} hasError={!!errors.phone} />
                    </LabelWithError>
                  </div>
                  <div className="sm:col-span-3">
                    <LabelWithError
                      title={isEdit ? strings.employees.form.passwordOptional : strings.employees.form.password}
                      error={errors.password?.message}
                    >
                      <StyledInput type="password" {...register('password')} hasError={!!errors.password} />
                    </LabelWithError>
                  </div>
                  <div className="sm:col-span-3">
                    <LabelWithError
                      title={strings.employees.form.passwordConfirmation}
                      error={errors.password_confirmation?.message}
                    >
                      <StyledInput
                        type="password"
                        {...register('password_confirmation')}
                        hasError={!!errors.password_confirmation}
                      />
                    </LabelWithError>
                  </div>
                </div>

                <div className="border-t border-gray-900/5">
                  <div className="grid grid-cols-1 gap-x-6 gap-y-6 p-4 sm:grid-cols-12">
                    <div className="sm:col-span-6">
                      <label htmlFor="comments" className="block text-sm font-medium text-gray-700">
                        {strings.employees.form.comments}
                      </label>
                      <div className="mt-1">
                        <textarea
                          {...register('comments')}
                          rows={4}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      <div className="relative mt-4 flex items-start py-1">
                        <div className="flex h-6 items-center">
                          <StyledSwitch name="is_active" control={control} />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label htmlFor="is_active" className="font-medium text-gray-900">
                            {strings.employees.form.active}
                          </label>
                        </div>
                      </div>
                      <div className="relative flex items-start py-1">
                        <div className="flex h-6 items-center">
                          <StyledSwitch name="is_group" control={control} />
                        </div>
                        <div className="ml-3 text-sm leading-6">
                          <label htmlFor="is_group" className="font-medium text-gray-900">
                            {strings.employees.form.group}
                          </label>
                        </div>
                      </div>
                    </div>
                    <div className="sm:col-span-6">
                      <label htmlFor="ips" className="mb-1 block text-sm font-medium text-gray-700">
                        {strings.employees.form.ipRanges}
                      </label>
                      {fields.map((item, index) => (
                        <div key={item.id} className="mb-4">
                          <div className="flex -space-x-px">
                            <div className="w-1/2 min-w-0 flex-1">
                              <input
                                type="text"
                                {...register(`ips.${index}.start`)}
                                defaultValue={item.start}
                                className="relative block w-full rounded-none rounded-l-md border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <input
                                type="text"
                                {...register(`ips.${index}.end`)}
                                defaultValue={item.end}
                                className="relative block w-full rounded-none border-0 bg-transparent py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:z-10 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="relative -ml-px inline-flex items-center gap-x-1.5 rounded-r-md px-3 py-1 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                            >
                              <TrashIcon className="-ml-0.5 h-5 w-5 text-gray-400" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                      ))}

                      <CommonButtonDefault sm onClick={() => append({ start: '', end: '' })} type="button" secondary>
                        {strings.employees.form.addIpRange}
                      </CommonButtonDefault>
                    </div>
                  </div>
                </div>

                <div className="border-t border-gray-900/5 p-4">
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700">{strings.employees.form.roles}</legend>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {availableRoles.map((role) => (
                        <label key={role.id} className="flex items-center space-x-3 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            value={role.name}
                            {...register('roles')}
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                          />
                          <span>{getEmployeeRoleLabel(role.name)}</span>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>

                <div className="flex justify-end space-x-4 rounded-b-lg bg-gray-50 px-4 py-3">
                  {isEdit && (
                    <CommonButtonDefault onClick={() => setConfirmDelete(true)} type="button" danger>
                      {strings.button.delete}
                    </CommonButtonDefault>
                  )}
                  <CommonButtonDefault onClick={() => navigate('/employees')} type="button" secondary>
                    {strings.button.cancel}
                  </CommonButtonDefault>
                  <CommonButtonDefault type="submit" primary>
                    {strings.button.save}
                  </CommonButtonDefault>
                </div>
              </form>
            </div>
            <CommonConfirmationModal
              open={confirmDelete}
              confirmed={() => onDelete()}
              cancelled={() => setConfirmDelete(false)}
              title={strings.common.confirmation.deleteUser.title}
              description={strings.common.confirmation.deleteUser.description}
              buttonConfirm={strings.common.yes}
              buttonCancel={strings.common.no}
            />
          </main>
        </>
      )}
    </div>
  );
}

EmployeesEditPageComponent.defaultProps = {
  isEdit: false,
};

export default EmployeesEditPageComponent;
